import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { queryKeys } from '../lib/query-client';
import { Button, Text, Caption, LoadingSpinner, Tooltip, Modal, tokens } from '../design-system';
import { useUpdatePlanScope, useCreatePlanFromIdea, useGeneratePlan, useUpdatePlanLifecycle } from '../hooks/api/useIdeas';
import { useAnalyzeIdeaEdit, useApplyIdeaEdit } from '../hooks/api/useIdeaEdit';
import IdeaCascadeModal from './IdeaCascadeModal';
import { useDeleteTask } from '../hooks/api/useTasks';
import { useApp } from '../contexts/AppContext';
import { TrashIcon } from '@heroicons/react/24/outline';
import RoadmapPushModal from './RoadmapPushModal';

// ─── Cottagecore tokens ────────────────────────────────────────────────────────

const T = {
  paper:      '#F7EFD8',
  paperDeep:  '#EEE2BF',
  paperSoft:  '#FDF7E5',
  card:       '#FFFBEC',
  ink:        '#2E2109',
  ink2:       '#4A3510',
  inkMuted:   '#6E5A38',
  inkFaint:   '#A39068',
  rule:       '#D7C9A1',
  ruleStrong: '#BFAB79',
  orange:     '#EA7C1F',
  orangeDark: '#C25F0E',
  moss:       '#6B7A3E',
  mossDark:   '#4E5A29',
  mossSoft:   '#D7DCB9',
  capRed:     '#C14A33',
  capRedSoft: '#F2C7BA',
  roofSoft:   '#F2D1B3',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sh3: '0 3px 0 rgba(46,33,9,0.05), 0 20px 36px -12px rgba(46,33,9,0.18)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

// ─── Config ────────────────────────────────────────────────────────────────

const PRIORITY_CONFIG = {
  must_have:    { label: 'Must Have',    color: T.orange,   bg: T.roofSoft },
  should_have:  { label: 'Should Have',  color: T.moss,     bg: T.mossSoft },
  nice_to_have: { label: 'Nice to Have', color: T.inkMuted, bg: T.paperDeep },
};

const STATUS_CONFIG = {
  planned:     { label: 'Planned',     color: T.inkFaint },
  in_progress: { label: 'In Progress', color: T.orange },
  shipped:     { label: 'Shipped',     color: T.moss },
  blocked:     { label: 'Blocked',     color: T.capRed },
};

const getFeatureStatus = (feature) => feature.status || 'planned';

const SCOPE_CONFIG = {
  mvp:     { label: 'MVP',     color: T.moss },
  full:    { label: 'Full',    color: T.orange },
  feature: { label: 'Feature', color: T.capRed },
  custom:  { label: 'Custom',  color: T.inkMuted },
};

const LIFECYCLE_CONFIG = {
  planned:     { label: 'Planned',     color: T.inkFaint },
  in_progress: { label: 'In Progress', color: T.orange },
  shipped:     { label: 'Shipped',     color: T.moss },
  blocked:     { label: 'Blocked',     color: T.capRed },
  archived:    { label: 'Archived',    color: T.inkFaint },
};

const STATUS_DOT = {
  submitted:  T.inkFaint,
  processing: T.orange,
  completed:  T.moss,
  error:      T.capRed,
};

const COLUMN_MIN_WIDTH = 240;

// ─── Feature Detail Panel ─────────────────────────────────────────────────

// Reusable inline-editable field wrapper — shows content normally, highlights on hover,
// switches to an input/textarea/dropdown on click.
const InlineField = ({ label, children }) => (
  <div style={{ marginBottom: '16px' }}>
    {label && (
      <div style={{ fontSize: '10px', fontWeight: 700, color: T.inkMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
        {label}
      </div>
    )}
    {children}
  </div>
);

const editableHoverStyle = {
  borderRadius: T.r2,
  cursor: 'text',
  transition: 'background-color 0.1s',
};

const FeatureDetailPanel = ({ feature, allFeatures, ideaId, onClose, onSaved }) => {
  // Local working copy — updated field-by-field and saved immediately
  const [draft, setDraft] = useState(() => ({
    ...feature,
    status: getFeatureStatus(feature),
    status_note: feature.status_note || '',
    user_stories: [...(feature.user_stories || [])],
  }));

  // Sync draft if the feature prop changes (e.g. after a save from another source)
  const prevFeatureRef = useRef(feature);
  useEffect(() => {
    if (feature !== prevFeatureRef.current) {
      prevFeatureRef.current = feature;
      setDraft({
        ...feature,
        status: getFeatureStatus(feature),
        status_note: feature.status_note || '',
        user_stories: [...(feature.user_stories || [])],
      });
    }
  }, [feature]);

  // Which text field is currently being edited
  const [editingField, setEditingField] = useState(null); // 'name' | 'description' | 'status_note' | null
  const [editingStoryIdx, setEditingStoryIdx] = useState(null);

  // Status/priority dropdowns
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showPriorityMenu, setShowPriorityMenu] = useState(false);
  const statusMenuRef = useRef(null);
  const priorityMenuRef = useRef(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cascadeModal, setCascadeModal] = useState({ open: false, data: null, pendingFeatures: null, afterApply: null });
  const [pendingDeleteData, setPendingDeleteData] = useState(null); // { features, version }

  const analyzeMutation = useAnalyzeIdeaEdit();
  const applyMutation = useApplyIdeaEdit();

  // Close menus on outside click
  useEffect(() => {
    if (!showStatusMenu && !showPriorityMenu) return;
    const handler = (e) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(e.target)) setShowStatusMenu(false);
      if (priorityMenuRef.current && !priorityMenuRef.current.contains(e.target)) setShowPriorityMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showStatusMenu, showPriorityMenu]);

  // Run cascade analysis and either show the modal (if findings) or silently apply.
  const doSaveWithCascade = useCallback(async (newFeatures, afterApply) => {
    try {
      const result = await analyzeMutation.mutateAsync({
        ideaId,
        section: 'core_features',
        changes: { value: newFeatures },
      });
      if (result.cascade_changes?.length > 0) {
        setCascadeModal({ open: true, data: result, pendingFeatures: newFeatures, afterApply });
      } else {
        await applyMutation.mutateAsync({
          ideaId,
          section: 'core_features',
          changes: { value: newFeatures },
          approvedCascades: [],
          expectedVersion: result.current_version,
        });
        afterApply?.();
      }
    } catch {}
  }, [ideaId, analyzeMutation, applyMutation]);

  // Apply the cascade changes selected in the modal.
  const handleCascadeApply = useCallback(async (approvedCascades) => {
    try {
      await applyMutation.mutateAsync({
        ideaId,
        section: 'core_features',
        changes: { value: cascadeModal.pendingFeatures },
        approvedCascades,
        expectedVersion: cascadeModal.data?.current_version,
      });
      const cb = cascadeModal.afterApply;
      setCascadeModal({ open: false, data: null, pendingFeatures: null, afterApply: null });
      cb?.();
    } catch {}
  }, [ideaId, applyMutation, cascadeModal]);

  // Save the current draft to the server
  const saveField = useCallback(async (updatedDraft) => {
    const features = allFeatures.map(f => f.name === feature.name ? updatedDraft : f);
    await doSaveWithCascade(features, () => onSaved?.(updatedDraft));
  }, [allFeatures, feature.name, doSaveWithCascade, onSaved]);

  // Dropdown field change — save immediately
  const changeDropdown = useCallback(async (field, value) => {
    const updated = { ...draft, [field]: value };
    setDraft(updated);
    setShowStatusMenu(false);
    setShowPriorityMenu(false);
    await saveField(updated);
  }, [draft, saveField]);

  // Text field: commit on blur or Enter (for single-line)
  const commitTextField = useCallback(async (field, value) => {
    const trimmed = typeof value === 'string' ? value : value;
    const updated = { ...draft, [field]: trimmed };
    setDraft(updated);
    setEditingField(null);
    if (JSON.stringify(updated) !== JSON.stringify(draft)) {
      await saveField(updated);
    }
  }, [draft, saveField]);

  // Story editing
  const commitStory = useCallback(async (i, value) => {
    const stories = [...draft.user_stories];
    stories[i] = value;
    const updated = { ...draft, user_stories: stories };
    setDraft(updated);
    setEditingStoryIdx(null);
    await saveField(updated);
  }, [draft, saveField]);

  const removeStory = useCallback(async (i) => {
    const stories = draft.user_stories.filter((_, idx) => idx !== i);
    const updated = { ...draft, user_stories: stories };
    setDraft(updated);
    setEditingStoryIdx(null);
    await saveField(updated);
  }, [draft, saveField]);

  const addStory = useCallback(async () => {
    const stories = [...draft.user_stories, ''];
    const updated = { ...draft, user_stories: stories };
    setDraft(updated);
    setEditingStoryIdx(stories.length - 1);
    // Don't save yet — user needs to type something
  }, [draft]);

  // Analyze cascade effects first, then confirm or show findings.
  const handleDeleteAnalyze = useCallback(async () => {
    const remainingFeatures = allFeatures.filter(f => f.name !== feature.name);
    try {
      const result = await analyzeMutation.mutateAsync({
        ideaId,
        section: 'core_features',
        changes: { value: remainingFeatures },
      });
      if (result.cascade_changes?.length > 0) {
        // Findings present — cascade modal serves as confirmation.
        setCascadeModal({
          open: true,
          data: result,
          pendingFeatures: remainingFeatures,
          afterApply: () => onClose(),
        });
      } else {
        // No findings — show simple confirmation.
        setPendingDeleteData({ features: remainingFeatures, version: result.current_version });
        setShowDeleteModal(true);
      }
    } catch {
      // Analysis failed — fall back to simple confirmation without version lock.
      setPendingDeleteData({ features: remainingFeatures, version: null });
      setShowDeleteModal(true);
    }
  }, [allFeatures, feature.name, ideaId, analyzeMutation, onClose]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingDeleteData) return;
    try {
      await applyMutation.mutateAsync({
        ideaId,
        section: 'core_features',
        changes: { value: pendingDeleteData.features },
        approvedCascades: [],
        expectedVersion: pendingDeleteData.version,
      });
      setShowDeleteModal(false);
      onClose();
    } catch {}
  }, [ideaId, applyMutation, pendingDeleteData, onClose]);

  const currentStatus = draft.status || 'planned';
  const statusCfg = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.planned;
  const priorityCfg = PRIORITY_CONFIG[draft.priority] || PRIORITY_CONFIG.nice_to_have;

  const needsStatusNote = currentStatus === 'in_progress' || currentStatus === 'blocked';

  return (
    <div style={{
      width: 380,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: T.paperSoft,
      borderLeft: `1px solid ${T.ruleStrong}`,
      borderRadius: `0 ${T.r4} ${T.r4} 0`,
      overflow: 'hidden',
      boxShadow: T.sh2,
    }}>
      {/* Header — name is inline-editable */}
      <div style={{
        padding: `12px 16px`,
        borderBottom: `1px solid ${T.rule}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        gap: '8px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: statusCfg.color, flexShrink: 0 }} />
          {editingField === 'name' ? (
            <input
              autoFocus
              defaultValue={draft.name}
              onBlur={e => commitTextField('name', e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setEditingField(null); } }}
              style={{
                flex: 1, minWidth: 0,
                background: T.paperSoft,
                border: `1px solid ${T.orange}`,
                borderRadius: T.r2,
                color: T.ink,
                fontSize: '13px',
                fontWeight: 600,
                padding: '2px 6px',
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
          ) : (
            <span
              onClick={() => setEditingField('name')}
              title="Click to edit"
              style={{
                fontWeight: 600,
                color: T.ink,
                fontSize: '13px',
                cursor: 'text',
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                padding: '2px 4px',
                marginLeft: -4,
                borderRadius: T.r2,
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = T.paperDeep; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {draft.name}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', color: T.inkMuted, cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: `4px 8px`, borderRadius: T.r2, flexShrink: 0 }}
        >×</button>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>

        {/* Status + Priority — click-to-open dropdowns */}
        <InlineField>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>

            {/* Status dropdown */}
            <div ref={statusMenuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowStatusMenu(v => !v)}
                style={{
                  fontSize: '12px', fontWeight: 700, color: statusCfg.color,
                  backgroundColor: `${statusCfg.color}20`, border: `1px solid ${statusCfg.color}55`,
                  borderRadius: T.r2, padding: '3px 10px',
                  cursor: 'pointer', fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: '5px',
                }}
              >
                {statusCfg.label} <span style={{ fontSize: '9px', opacity: 0.7 }}>▾</span>
              </button>
              {showStatusMenu && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, zIndex: 50,
                  backgroundColor: T.card,
                  border: `1px solid ${T.rule}`,
                  borderRadius: T.r3,
                  padding: '4px', minWidth: 140,
                  boxShadow: T.sh3,
                  marginTop: '4px',
                }}>
                  {Object.entries(STATUS_CONFIG).map(([value, cfg]) => {
                    const isCurrent = currentStatus === value;
                    return (
                      <button key={value} onClick={() => changeDropdown('status', value)} style={{
                        display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                        padding: '6px 8px',
                        backgroundColor: isCurrent ? `${cfg.color}22` : 'transparent',
                        border: isCurrent ? `1px solid ${cfg.color}44` : '1px solid transparent',
                        borderRadius: T.r2, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                      }}
                        onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.backgroundColor = T.paperDeep; }}
                        onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: cfg.color, flexShrink: 0 }} />
                        <span style={{ fontSize: '12px', color: isCurrent ? cfg.color : T.ink, fontWeight: isCurrent ? 600 : 400 }}>{cfg.label}</span>
                        {isCurrent && <span style={{ marginLeft: 'auto', fontSize: '10px', color: cfg.color }}>✓</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Priority dropdown */}
            <div ref={priorityMenuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowPriorityMenu(v => !v)}
                style={{
                  fontSize: '12px', fontWeight: 700, color: priorityCfg.color,
                  backgroundColor: priorityCfg.bg, border: `1px solid ${priorityCfg.color}55`,
                  borderRadius: T.r2, padding: '3px 10px',
                  cursor: 'pointer', fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: '5px',
                }}
              >
                {priorityCfg.label} <span style={{ fontSize: '9px', opacity: 0.7 }}>▾</span>
              </button>
              {showPriorityMenu && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, zIndex: 50,
                  backgroundColor: T.card,
                  border: `1px solid ${T.rule}`,
                  borderRadius: T.r3,
                  padding: '4px', minWidth: 140,
                  boxShadow: T.sh3,
                  marginTop: '4px',
                }}>
                  {Object.entries(PRIORITY_CONFIG).map(([value, cfg]) => {
                    const isCurrent = (draft.priority || 'nice_to_have') === value;
                    return (
                      <button key={value} onClick={() => changeDropdown('priority', value)} style={{
                        display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                        padding: '6px 8px',
                        backgroundColor: isCurrent ? `${cfg.color}22` : 'transparent',
                        border: isCurrent ? `1px solid ${cfg.color}44` : '1px solid transparent',
                        borderRadius: T.r2, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                      }}
                        onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.backgroundColor = T.paperDeep; }}
                        onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        <span style={{ fontSize: '12px', color: isCurrent ? cfg.color : T.ink, fontWeight: isCurrent ? 600 : 400 }}>{cfg.label}</span>
                        {isCurrent && <span style={{ marginLeft: 'auto', fontSize: '10px', color: cfg.color }}>✓</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </InlineField>

        {/* Status note — shown when in_progress or blocked, or when a note already exists */}
        {(needsStatusNote || draft.status_note) && (
          <InlineField label={currentStatus === 'in_progress' ? 'Progress Note' : 'Status Note'}>
            {editingField === 'status_note' ? (
              <textarea
                autoFocus
                defaultValue={draft.status_note}
                onBlur={e => commitTextField('status_note', e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') setEditingField(null); }}
                placeholder={currentStatus === 'in_progress' ? "What's been built and what's still missing?" : "What's blocking this feature?"}
                rows={3}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: T.paperSoft,
                  border: `1px solid ${T.orange}`,
                  borderRadius: T.r2,
                  color: T.ink, fontSize: '13px',
                  padding: '8px', outline: 'none', resize: 'vertical',
                  fontFamily: 'inherit', lineHeight: '1.55',
                }}
              />
            ) : (
              <div
                onClick={() => setEditingField('status_note')}
                title="Click to edit"
                style={{
                  ...editableHoverStyle,
                  padding: '12px',
                  backgroundColor: draft.status_note ? `${statusCfg.color}15` : T.paperDeep,
                  borderLeft: draft.status_note ? `3px solid ${statusCfg.color}` : `3px solid ${T.rule}`,
                  borderRadius: `0 ${T.r3} ${T.r3} 0`,
                  minHeight: 48,
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = draft.status_note ? `${statusCfg.color}22` : T.paperDeep; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = draft.status_note ? `${statusCfg.color}15` : T.paperDeep; }}
              >
                {draft.status_note ? (
                  <span style={{ fontSize: '13px', color: T.ink, lineHeight: '1.55' }}>{draft.status_note}</span>
                ) : (
                  <span style={{ fontSize: '13px', color: T.inkMuted, fontStyle: 'italic' }}>
                    {currentStatus === 'in_progress' ? "What's been built and what's still missing?" : "What's blocking this feature?"}
                  </span>
                )}
              </div>
            )}
          </InlineField>
        )}

        {/* Description */}
        <InlineField label="Description">
          {editingField === 'description' ? (
            <textarea
              autoFocus
              defaultValue={draft.description || ''}
              onBlur={e => commitTextField('description', e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') setEditingField(null); }}
              placeholder="Describe this feature..."
              rows={4}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: T.paperSoft,
                border: `1px solid ${T.orange}`,
                borderRadius: T.r2,
                color: T.ink, fontSize: '13px',
                padding: '8px', outline: 'none', resize: 'vertical',
                fontFamily: 'inherit', lineHeight: '1.65',
              }}
            />
          ) : (
            <div
              onClick={() => setEditingField('description')}
              title="Click to edit"
              style={{
                ...editableHoverStyle,
                fontSize: '13px', color: draft.description ? T.ink : T.inkMuted,
                lineHeight: '1.65', padding: '6px 8px', marginLeft: -8,
                fontStyle: draft.description ? 'normal' : 'italic',
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = T.paperDeep; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {draft.description || 'Add a description...'}
            </div>
          )}
        </InlineField>

        {/* User Stories */}
        <InlineField label={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>User Stories</span>
            <button
              onClick={addStory}
              style={{
                background: 'none', border: `1px solid ${T.rule}`,
                borderRadius: T.r2, color: T.inkMuted,
                cursor: 'pointer', fontSize: '11px', padding: '1px 7px', fontFamily: 'inherit',
                textTransform: 'none', letterSpacing: 'normal', fontWeight: 400,
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = T.orange; e.currentTarget.style.color = T.orange; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.rule; e.currentTarget.style.color = T.inkMuted; }}
            >+ Add</button>
          </div>
        }>
          {draft.user_stories.length === 0 ? (
            <div
              onClick={addStory}
              style={{
                padding: '16px', textAlign: 'center',
                border: `1px dashed ${T.rule}`,
                borderRadius: T.r3, cursor: 'pointer',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = T.orange; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.rule; }}
            >
              <span style={{ fontSize: '12px', color: T.inkMuted }}>No user stories yet — click to add one</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {draft.user_stories.map((story, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  {editingStoryIdx === i ? (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <textarea
                        autoFocus
                        defaultValue={story}
                        onBlur={e => commitStory(i, e.target.value)}
                        onKeyDown={e => { if (e.key === 'Escape') setEditingStoryIdx(null); }}
                        placeholder="As a [user], I want to [action] so that [benefit]..."
                        rows={2}
                        style={{
                          flex: 1, boxSizing: 'border-box',
                          background: T.paperSoft,
                          border: `1px solid ${T.orange}`,
                          borderRadius: T.r2,
                          color: T.ink, fontSize: '13px',
                          padding: '8px', outline: 'none', resize: 'vertical',
                          fontFamily: 'inherit', lineHeight: '1.55',
                        }}
                      />
                      <button
                        onClick={() => removeStory(i)}
                        style={{
                          flexShrink: 0, background: 'none', border: `1px solid ${T.rule}`,
                          borderRadius: T.r2, color: T.inkMuted, cursor: 'pointer',
                          width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '14px', marginTop: '2px', fontFamily: 'inherit',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = T.capRed; e.currentTarget.style.color = T.capRed; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = T.rule; e.currentTarget.style.color = T.inkMuted; }}
                      >×</button>
                    </div>
                  ) : (
                    <div
                      onClick={() => setEditingStoryIdx(i)}
                      title="Click to edit"
                      style={{
                        ...editableHoverStyle,
                        padding: `8px 12px`,
                        backgroundColor: T.card,
                        border: `1px solid ${T.rule}`,
                        borderRadius: T.r3,
                        fontSize: '13px', color: T.ink, lineHeight: '1.55',
                        cursor: 'text',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = T.inkMuted; e.currentTarget.style.backgroundColor = T.paperDeep; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = T.rule; e.currentTarget.style.backgroundColor = T.card; }}
                    >
                      {story || <span style={{ color: T.inkMuted, fontStyle: 'italic' }}>Empty story — click to edit</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </InlineField>
      </div>

      {/* Footer — just delete now */}
      <div style={{
        padding: `12px 16px`,
        borderTop: `1px solid ${T.rule}`,
        flexShrink: 0,
      }}>
        <Button
          variant="danger"
          onClick={handleDeleteAnalyze}
          loading={analyzeMutation.isPending}
          disabled={analyzeMutation.isPending || applyMutation.isPending}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
        >
          <TrashIcon style={{ width: 15, height: 15 }} />
          Delete Feature
        </Button>
      </div>

      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Delete Feature" size="sm">
        <Text style={{ marginBottom: '16px', color: T.ink }}>
          Are you sure you want to delete <strong>{feature.name}</strong>? This cannot be undone.
        </Text>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={applyMutation.isPending}>Delete</Button>
        </div>
      </Modal>

      <IdeaCascadeModal
        isOpen={cascadeModal.open}
        onClose={() => setCascadeModal({ open: false, data: null, pendingFeatures: null, afterApply: null })}
        directChange={cascadeModal.data?.direct_change}
        cascadeChanges={cascadeModal.data?.cascade_changes || []}
        affectedPlans={cascadeModal.data?.affected_plans || []}
        summary={cascadeModal.data?.summary}
        warnings={cascadeModal.data?.warnings || []}
        onApply={handleCascadeApply}
        isApplying={applyMutation.isPending}
      />
    </div>
  );
};

// ─── Feature Card ───────────────────────────────────────────────────────────

const FeatureCard = ({ feature, isOverlay = false, onOpenDetail }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: feature.name,
    data: { feature },
  });

  // Track whether a drag actually occurred so clicks after drags don't open the panel
  const hasDragged = useRef(false);
  const prevIsDragging = useRef(false);
  useEffect(() => {
    if (isDragging && !prevIsDragging.current) {
      hasDragged.current = true;
    }
    if (!isDragging && prevIsDragging.current) {
      // Reset after any pending click events fire
      const t = setTimeout(() => { hasDragged.current = false; }, 100);
      return () => clearTimeout(t);
    }
    prevIsDragging.current = isDragging;
  }, [isDragging]);

  const handleClick = useCallback(() => {
    if (!hasDragged.current && !isOverlay) {
      onOpenDetail?.();
    }
  }, [isOverlay, onOpenDetail]);

  const priority = PRIORITY_CONFIG[feature.priority] || PRIORITY_CONFIG.nice_to_have;

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      title={feature.description || undefined}
      style={{
        transform: transform ? CSS.Translate.toString(transform) : undefined,
        opacity: isDragging ? 0.4 : 1,
        padding: '12px 14px',
        backgroundColor: T.card,
        border: `1px solid ${isOverlay ? T.orange : T.rule}`,
        borderRadius: T.r3,
        cursor: isOverlay ? 'grabbing' : 'grab',
        userSelect: 'none',
        boxShadow: isOverlay ? T.sh3 : T.sh1,
        touchAction: 'none',
        transition: isDragging ? 'none' : 'box-shadow 0.1s ease',
        fontFamily: T.sans,
      }}
      onMouseEnter={e => { if (!isDragging && !isOverlay) e.currentTarget.style.boxShadow = T.sh2; }}
      onMouseLeave={e => { if (!isDragging && !isOverlay) e.currentTarget.style.boxShadow = T.sh1; }}
    >
      {/* Top row: priority abbreviation + status */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontFamily: T.mono, fontSize: 10, fontWeight: 800, color: priority.color, letterSpacing: 0.5 }}>
          {priority.label.split(' ')[0].charAt(0)}-{String(feature.name || '').slice(0, 2).toUpperCase()}
        </span>
        {(() => {
          const status = getFeatureStatus(feature);
          const sCfg = STATUS_CONFIG[status];
          if (!sCfg || status === 'planned') return null;
          return <span style={{ fontSize: 10, color: sCfg.color, fontWeight: 700 }}>{sCfg.label}</span>;
        })()}
      </div>
      {/* Feature name */}
      <div style={{ fontSize: 14, fontWeight: 700, color: T.ink, lineHeight: 1.35, marginBottom: feature.description ? 6 : 0 }}>
        {feature.name}
      </div>
      {/* Description */}
      {feature.description && (
        <div style={{
          fontSize: 12, color: T.ink2, lineHeight: 1.5,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {feature.description}
        </div>
      )}
    </div>
  );
};

// ─── Plan Column ─────────────────────────────────────────────────────────────

const PlanColumn = ({ id, plan, features, onOpenFeature, ideaId, onDeleted }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  const navigate = useNavigate();
  const generatePlan = useGeneratePlan();
  const updateScope = useUpdatePlanScope();
  const updateLifecycle = useUpdatePlanLifecycle();
  const deleteMutation = useDeleteTask();
  const { showSuccess, showError } = useApp();
  const [pushOpen, setPushOpen] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [headerHover, setHeaderHover] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showLifecycleMenu, setShowLifecycleMenu] = useState(false);
  const lifecycleMenuRef = useRef(null);

  // Close lifecycle menu on outside click
  useEffect(() => {
    if (!showLifecycleMenu) return;
    const handler = (e) => {
      if (lifecycleMenuRef.current && !lifecycleMenuRef.current.contains(e.target)) {
        setShowLifecycleMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showLifecycleMenu]);

  const handleLifecycleChange = async (newStatus) => {
    setShowLifecycleMenu(false);
    try {
      await updateLifecycle.mutateAsync({ planId: plan.id, ideaId, lifecycleStatus: newStatus });
    } catch {
      showError('Failed to update plan status');
    }
  };

  const handleDeleteConfirm = async () => {
    setShowConfirm(false);
    try {
      await deleteMutation.mutateAsync(plan.id);
      showSuccess('Plan deleted');
      onDeleted?.();
    } catch {
      showError('Failed to delete plan');
    }
  };
  const isBacklog = id === 'backlog';

  const scope = plan ? SCOPE_CONFIG[plan.scope] : null;

  const isProcessing = plan?.status === 'processing';
  const neverGenerated = plan?.status === 'submitted';
  const hasError = plan?.status === 'error';

  // Stale if the features currently in the column differ from what the plan
  // was last generated with (plan.selected_features). Dragging out and back
  // in resolves to the same set → no Re-run needed.
  const columnFeatureNames = features.map(f => f.name).sort();
  const generatedFeatureNames = (plan?.selected_features || []).slice().sort();
  const columnMatchesGenerated =
    columnFeatureNames.length === generatedFeatureNames.length &&
    columnFeatureNames.every((n, i) => n === generatedFeatureNames[i]);
  const isStale = plan?.status === 'completed' && !columnMatchesGenerated;

  const needsGenerate = (neverGenerated || hasError || isStale) && features.length > 0;

  const handleGenerate = async (e) => {
    e.stopPropagation();
    try {
      // Save the current column feature selection before generating
      await updateScope.mutateAsync({ planId: plan.id, selectedFeatures: columnFeatureNames });
      await generatePlan.mutateAsync({ planId: plan.id, ideaId });
    } catch {}
  };

  // Pick an accent color for this column based on scope/lifecycle
  const accentColor = isBacklog
    ? T.inkMuted
    : plan?.lifecycle_status === 'shipped' ? T.moss
    : plan?.lifecycle_status === 'blocked' ? T.capRed
    : plan?.lifecycle_status === 'in_progress' ? T.orange
    : scope?.color || T.inkMuted;

  const doneCount = features.filter(f => getFeatureStatus(f) === 'shipped').length;

  return (
    <div
      ref={setNodeRef}
      style={{
        minWidth: COLUMN_MIN_WIDTH,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderRadius: T.r4,
        border: `1px dashed ${isOver ? T.orange : T.ruleStrong}`,
        backgroundColor: isOver ? `${T.orange}08` : T.paperDeep,
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        overflow: 'hidden',
      }}>
      {/* Column header */}
      <div
        style={{
          padding: `14px 14px 12px`,
          borderBottom: `1px solid ${T.rule}`,
          flexShrink: 0,
          backgroundColor: T.card,
        }}
        onMouseEnter={() => setHeaderHover(true)}
        onMouseLeave={() => setHeaderHover(false)}
      >
        {isBacklog ? (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
              <div style={{ width: 4, height: 20, borderRadius: 2, background: T.inkFaint, flexShrink: 0, alignSelf: 'center' }} />
              <div style={{ fontSize: 18, fontWeight: 800, color: T.inkMuted, letterSpacing: '-0.3px' }}>Backlog</div>
            </div>
            <div style={{ fontSize: 12, color: T.inkMuted, marginBottom: 10, marginLeft: 14 }}>
              {features.length} unassigned feature{features.length !== 1 ? 's' : ''}
            </div>
          </>
        ) : (
          <div>
            {/* Title row: accent bar + plan name + lifecycle pill */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, minWidth: 0 }}>
              <div style={{ width: 4, height: 20, borderRadius: 2, background: accentColor, flexShrink: 0 }} />
              <div style={{
                fontSize: 18,
                fontWeight: 800,
                color: T.ink,
                letterSpacing: '-0.3px',
                lineHeight: 1.2,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>{plan.name}</div>
              {(() => {
                const lc = plan.lifecycle_status || 'planned';
                const lcCfg = LIFECYCLE_CONFIG[lc] || LIFECYCLE_CONFIG.planned;
                return (
                  <span style={{
                    fontFamily: T.mono,
                    fontSize: 9,
                    fontWeight: 800,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    color: lcCfg.color,
                    background: `${lcCfg.color}18`,
                    border: `1px solid ${lcCfg.color}40`,
                    borderRadius: T.full,
                    padding: '2px 8px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}>
                    {lcCfg.label}
                  </span>
                );
              })()}
            </div>
            {/* Info + actions row */}
            {isProcessing || generatePlan.isPending ? (
              <Caption style={{ fontSize: '10px', color: T.orange, marginLeft: 14 }}>
                Generating…
              </Caption>
            ) : hasError ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginLeft: 14 }}>
                <Caption style={{ fontSize: '10px', color: T.orange }}>
                  Generation failed
                </Caption>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <button
                    onClick={handleGenerate}
                    style={{
                      background: `${T.orange}15`,
                      border: `1px solid ${T.orange}40`,
                      borderRadius: T.r2,
                      color: T.orange,
                      cursor: 'pointer',
                      fontSize: '10px',
                      fontWeight: 600,
                      padding: '2px 7px',
                      fontFamily: 'inherit',
                      lineHeight: 1.5,
                    }}
                  >
                    Re-run ↻
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setShowConfirm(true); }} disabled={deleteMutation.isPending} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: '11px', fontFamily: 'inherit', color: T.inkMuted }} onMouseEnter={(e) => { e.currentTarget.style.color = T.capRed; }} onMouseLeave={(e) => { e.currentTarget.style.color = T.inkMuted; }}>
                    Delete
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginLeft: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                  <Caption style={{ fontSize: '10px', color: T.inkMuted, whiteSpace: 'nowrap' }}>
                    {features.length} feature{features.length !== 1 ? 's' : ''}
                  </Caption>
                  {syncStatus && (
                    <a
                      href={syncStatus.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: '10px',
                        color: T.moss,
                        fontWeight: 600,
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '2px',
                      }}
                    >
                      ✓ {syncStatus.label}
                    </a>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  {needsGenerate && (
                    <button
                      onClick={handleGenerate}
                      style={{
                        background: `${T.orange}15`,
                        border: `1px solid ${T.orange}40`,
                        borderRadius: T.r2,
                        color: T.orange,
                        cursor: 'pointer',
                        fontSize: '10px',
                        fontWeight: 600,
                        padding: '2px 7px',
                        fontFamily: 'inherit',
                        lineHeight: 1.5,
                      }}
                    >
                      {isStale ? 'Re-run ↻' : 'Generate →'}
                    </button>
                  )}
                  {!needsGenerate && plan?.status === 'completed' && !isStale && features.length > 0 && !(isProcessing || generatePlan.isPending) && (
                    <Tooltip content="Push features and stories to Jira or Trello" position="top">
                      <button
                        onClick={() => setPushOpen(true)}
                        style={{
                          background: `${T.moss}15`,
                          border: `1px solid ${T.moss}40`,
                          borderRadius: T.r2,
                          color: T.moss,
                          cursor: 'pointer',
                          fontSize: '10px',
                          fontWeight: 600,
                          padding: '2px 7px',
                          fontFamily: 'inherit',
                          lineHeight: 1.5,
                        }}
                      >
                        Push ↑
                      </button>
                    </Tooltip>
                  )}
                  {(plan.status === 'completed' || plan.status === 'processing') && (
                    <button onClick={() => navigate(`/plans/${plan.id}`)} style={{ background: 'none', border: 'none', color: T.orange, cursor: 'pointer', fontSize: '11px', padding: 0, lineHeight: 1, fontFamily: 'inherit', fontWeight: 700 }}>
                      Open →
                    </button>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); setShowConfirm(true); }} disabled={deleteMutation.isPending} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: '11px', fontFamily: 'inherit', color: T.inkMuted }} onMouseEnter={(e) => { e.currentTarget.style.color = T.capRed; }} onMouseLeave={(e) => { e.currentTarget.style.color = T.inkMuted; }}>
                    Delete
                  </button>
                </div>
              </div>
            )}
            {/* Progress sparkline */}
            <div style={{ height: 4, background: T.paperDeep, borderRadius: T.full, overflow: 'hidden', margin: '0 -14px -12px' }}>
              <div style={{ width: `${features.length ? (doneCount / features.length) * 100 : 0}%`, height: '100%', background: accentColor }} />
            </div>
          </div>
        )}
      </div>

      {/* Feature list */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: `8px 8px`,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          minHeight: 80,
        }}
      >
        {features.length === 0 ? (
          <div style={{
            flex: 1,
            minHeight: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px dashed ${isOver ? T.orange : T.rule}`,
            borderRadius: T.r3,
            transition: 'border-color 0.15s ease',
            opacity: 0.6,
          }}>
            <Caption style={{ fontSize: '11px', color: T.inkMuted }}>
              {isOver ? 'Release to assign' : 'Drop features here'}
            </Caption>
          </div>
        ) : (
          features.map(feature => (
            <FeatureCard
              key={feature.name}
              feature={feature}
              onOpenDetail={() => onOpenFeature(feature)}
            />
          ))
        )}
        {/* Add story placeholder */}
        {!isBacklog && (
          <button style={{
            background: 'transparent', border: `1px dashed ${T.rule}`, borderRadius: T.r3,
            color: T.inkFaint, fontSize: 12, padding: '8px', cursor: 'pointer',
            fontFamily: T.sans, width: '100%', marginTop: 4,
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.orange; e.currentTarget.style.color = T.orange; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.rule; e.currentTarget.style.color = T.inkFaint; }}
          >
            + Add story
          </button>
        )}
      </div>

      <Modal isOpen={showConfirm} onClose={() => setShowConfirm(false)} title="Delete Plan" size="sm">
        <Text style={{ marginBottom: '16px', color: T.ink }}>
          Are you sure you want to delete <strong>{plan?.name || 'this plan'}</strong>? This cannot be undone.
        </Text>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <Button variant="ghost" onClick={() => setShowConfirm(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={deleteMutation.isPending}>Delete</Button>
        </div>
      </Modal>

      <RoadmapPushModal
        plan={plan}
        isOpen={pushOpen}
        onClose={() => setPushOpen(false)}
        onSuccess={(integration, result) => {
          const label = integration === 'jira'
            ? `JIRA ${result.project_key}`
            : 'Trello';
          const url = integration === 'jira' ? result.project_url : result.board_url;
          setSyncStatus({ integration, label, url });
        }}
      />
    </div>
  );
};

// ─── New Plan Column ──────────────────────────────────────────────────────────

// ─── Add Plan Column ──────────────────────────────────────────────────────────

const AddPlanColumn = ({ ideaId, canCreate }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [name, setName] = useState('');
  const createPlan = useCreatePlanFromIdea();
  const inputRef = useRef(null);

  const start = () => {
    if (!canCreate) return;
    setName('');
    setIsAdding(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const cancel = () => { setName(''); setIsAdding(false); };

  const commit = async () => {
    const trimmed = name.trim();
    if (!trimmed) { cancel(); return; }
    try {
      await createPlan.mutateAsync({
        ideaId,
        planData: { name: trimmed, scope: 'custom', generate: false, selected_features: [] },
      });
    } catch {}
    cancel();
  };

  if (isAdding) {
    return (
      <div style={{
        minWidth: COLUMN_MIN_WIDTH,
        flex: 1,
        borderRadius: T.r4,
        border: `1px solid ${T.orange}`,
        backgroundColor: `${T.orange}08`,
        overflow: 'hidden',
      }}>
        <div style={{ padding: `12px 12px`, borderBottom: `1px solid ${T.rule}` }}>
          <input
            ref={inputRef}
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Plan name..."
            onKeyDown={e => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') cancel();
            }}
            onBlur={commit}
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              outline: 'none',
              color: T.ink,
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: 'inherit',
            }}
          />
          <Caption style={{ fontSize: '11px', color: T.inkMuted, marginTop: '2px' }}>
            Enter to create · Esc to cancel
          </Caption>
        </div>
        <div style={{
          padding: '12px',
          minHeight: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Caption style={{ fontSize: '11px', color: T.inkMuted }}>
            Drag features in after creating
          </Caption>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={start}
      style={{
        minWidth: COLUMN_MIN_WIDTH,
        flex: 1,
        alignSelf: 'flex-start',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 48,
        borderRadius: T.r4,
        border: `1px dashed ${T.rule}`,
        cursor: canCreate ? 'pointer' : 'default',
        opacity: canCreate ? 1 : 0.35,
        transition: 'border-color 0.15s, background-color 0.15s',
        fontSize: '20px',
        color: T.inkMuted,
      }}
      title={canCreate ? 'Add a plan' : 'Complete spec first'}
      onMouseEnter={e => {
        if (canCreate) {
          e.currentTarget.style.borderColor = T.orange;
          e.currentTarget.style.backgroundColor = `${T.orange}08`;
          e.currentTarget.style.color = T.orange;
        }
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = T.rule;
        e.currentTarget.style.backgroundColor = 'transparent';
        e.currentTarget.style.color = T.inkMuted;
      }}
    >
      +
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const buildAssignments = (plans, features) => {
  const assignments = {};
  features.forEach(f => { assignments[f.name] = 'backlog'; });
  plans.forEach(plan => {
    (plan.selected_features || []).forEach(name => {
      if (Object.prototype.hasOwnProperty.call(assignments, name)) {
        assignments[name] = plan.id;
      }
    });
  });
  return assignments;
};

const IdeaRoadmap = ({ idea, plans, plansLoading, onRefetch }) => {
  const [activeFeature, setActiveFeature] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const allFeatures = useMemo(
    () => idea?.idea_spec?.core_features || [],
    [idea?.idea_spec?.core_features]
  );

  const visiblePlans = useMemo(
    () => showArchived ? plans : plans.filter(p => (p.lifecycle_status || 'planned') !== 'archived'),
    [plans, showArchived]
  );

  const archivedCount = useMemo(
    () => plans.filter(p => (p.lifecycle_status || 'planned') === 'archived').length,
    [plans]
  );

  // Always build assignments from ALL plans so archiving a plan doesn't
  // move its features to the backlog — they stay assigned and just become
  // invisible until "Show archived" is toggled on.
  const [featureAssignments, setFeatureAssignments] = useState(
    () => buildAssignments(plans, allFeatures)
  );

  // Sync local state when server data changes (e.g. after generate completes)
  const plansRef = useRef(plans);
  useEffect(() => {
    if (plans !== plansRef.current) {
      plansRef.current = plans;
      setFeatureAssignments(buildAssignments(plans, allFeatures));
    }
  }, [plans, allFeatures]);

  const columns = useMemo(() => {
    // Only show in backlog if not assigned to any plan (including hidden ones)
    const backlogFeatures = allFeatures.filter(f =>
      !featureAssignments[f.name] || featureAssignments[f.name] === 'backlog'
    );
    const planCols = visiblePlans.map(plan => ({
      id: plan.id,
      plan,
      features: allFeatures.filter(f => featureAssignments[f.name] === plan.id),
    }));
    return [
      { id: 'backlog', plan: null, features: backlogFeatures },
      ...planCols,
    ];
  }, [allFeatures, featureAssignments, visiblePlans]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const handleDragStart = useCallback(({ active }) => {
    const feature = allFeatures.find(f => f.name === active.id);
    setActiveFeature(feature || null);
  }, [allFeatures]);

  const handleDragEnd = useCallback(async ({ active, over }) => {
    setActiveFeature(null);
    if (!over) {
      return;
    }

    const featureName = active.id;
    const targetColId = over.id;
    const sourceColId = featureAssignments[featureName] || 'backlog';

    if (sourceColId === targetColId) {
      return;
    }

    // Update local state only — selected_features on the plan is not changed until
    // the user clicks Generate/Re-run, so we can compare column state to
    // plan.selected_features to know if a re-run is actually needed.
    setFeatureAssignments(prev => ({ ...prev, [featureName]: targetColId }));
  }, [featureAssignments]);

  const canCreatePlan = idea?.status === 'specified' || idea?.status === 'active';

  if (plansLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <LoadingSpinner />
      </div>
    );
  }

  const hasSpec = !!idea?.idea_spec;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 0 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 20, flexShrink: 0 }}>
        <div>
          <div style={{ fontFamily: T.hand, fontSize: 22, color: T.orange, lineHeight: 1 }}>shape it —</div>
          <h1 style={{ fontSize: 30, fontWeight: 800, color: T.ink, margin: '4px 0 6px', letterSpacing: '-0.6px', fontFamily: T.sans }}>Roadmap</h1>
          <p style={{ fontSize: 14, color: T.ink2, margin: 0, fontFamily: T.sans }}>Drag a feature across columns to re-prioritize.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {hasSpec && allFeatures.length > 0 && (
            <>
              <span style={{ fontFamily: T.mono, fontSize: 11, fontWeight: 700, color: T.inkMuted, background: T.paperDeep, border: `1px solid ${T.rule}`, borderRadius: T.full, padding: '3px 10px' }}>
                {allFeatures.length} feature{allFeatures.length !== 1 ? 's' : ''}
              </span>
              <span style={{ fontFamily: T.mono, fontSize: 11, fontWeight: 700, color: T.moss, background: T.mossSoft, border: `1px solid ${T.moss}40`, borderRadius: T.full, padding: '3px 10px' }}>
                {visiblePlans.length} plan{visiblePlans.length !== 1 ? 's' : ''}
              </span>
            </>
          )}
          {!hasSpec && (
            <span style={{ fontSize: 12, color: T.orange, fontFamily: T.sans }}>Complete discovery to generate features</span>
          )}
          {archivedCount > 0 && (
            <button
              onClick={() => setShowArchived(v => !v)}
              style={{
                background: showArchived ? `${T.inkMuted}20` : 'none',
                border: `1px dashed ${T.rule}`,
                borderRadius: T.r2,
                color: T.inkMuted,
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: 700,
                padding: '4px 10px',
                fontFamily: T.sans,
                lineHeight: 1.5,
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = T.inkMuted; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.rule; }}
            >
              {showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
            </button>
          )}
        </div>
      </div>

      {/* Kanban + inline detail panel */}
      <div style={{ display: 'flex', flex: 1, gap: '12px', overflow: 'hidden' }}>
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns.length + 1}, minmax(${COLUMN_MIN_WIDTH}px, 1fr))`,
            gap: '12px',
            flex: 1,
            overflowX: 'auto',
            overflowY: 'hidden',
            paddingBottom: '8px',
            alignItems: 'flex-start',
          }}>
            {columns.map(col => (
              <PlanColumn
                key={col.id}
                id={col.id}
                plan={col.plan}
                features={col.features}
                onOpenFeature={(feature) => setSelectedFeature(prev => prev?.name === feature.name ? null : feature)}
                ideaId={idea?.id}
                onDeleted={onRefetch}
              />
            ))}

            <AddPlanColumn ideaId={idea?.id} canCreate={canCreatePlan} />
          </div>

          <DragOverlay dropAnimation={null}>
            {activeFeature ? (
              <FeatureCard feature={activeFeature} isOverlay />
            ) : null}
          </DragOverlay>
        </DndContext>

        {/* Inline feature detail panel — no backdrop, no overlay */}
        {selectedFeature && (
          <FeatureDetailPanel
            key={selectedFeature.name}
            feature={selectedFeature}
            allFeatures={allFeatures}
            ideaId={idea.id}
            onClose={() => setSelectedFeature(null)}
            onSaved={(updated) => setSelectedFeature(updated)}
          />
        )}
      </div>
    </div>
  );
};

export default IdeaRoadmap;
